import { cqAPI, getBid } from "../../lib/Auth";
import {
  Switch,
  Text,
  Box,
  Flex,
  useColorMode,
  useToast,
  Button,
  Modal,
  ModalOverlay,
  useColorModeValue,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  useDisclosure,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Radio,
  RadioGroup,
  VStack,
  Checkbox,
  CheckboxGroup,
  Spinner,
  MenuButton,
  Menu,
  MenuList,
  MenuItem,
  Tooltip,
} from "@chakra-ui/react";
import Select from "react-select";
import React, { useEffect, useState, useRef } from "react";
import { AddIcon, DeleteIcon, CheckIcon, ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import EditableText from "../EditableText/EditableText";
import { useNavigate, useParams } from "react-router-dom";
import _ from "lodash";
import HistorySettingsControls from "./HistorySettingsControls";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import theme from "../../theme";

const GenericSettings = ({
  configuration,
  onDeleteField,
  onAddInput,
  onChange,
  onReorderSections,
  onReorderQuestions,
}) => {
  const [settings, setSettings] = useState({});
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const { colorMode } = useColorMode();
  const [reorderSectionsModalOpen, setReorderSectionsModalOpen] = useState(false);
  const [sectionIdxToReorder, setSectionIdxToReorder] = useState(null);
  const [reorderQuestionsModalOpen, setReorderQuestionsModalOpen] = useState(false);

  // Local state for reordering sections without mutating props until Save
  const [reorderList, setReorderList] = useState([]);
  const [reorderIndexList, setReorderIndexList] = useState([]);
  const [reorderQuestions, setReorderedQuestions] = useState([])
  const [reorderQuestionIndexList, setReorderQuestionIndexList] = useState([])

  useEffect(() => {
    if (reorderSectionsModalOpen) {
      // create a shallow copy to reorder for display
      const list = configuration ? [...configuration] : [];
      setReorderList(list);
      setReorderIndexList(list.map((_, i) => i));
    }
  }, [reorderSectionsModalOpen, configuration]);

  useEffect(() => {
    if (reorderQuestionsModalOpen) {
      const idx = sectionIdxToReorder;
      const inputs =
        idx !== null && idx !== undefined && configuration && configuration[idx]
          ? configuration[idx].inputs || []
          : [];
      setReorderedQuestions([...inputs]);
      setReorderQuestionIndexList(inputs.map((_, i) => i));
    }
  }, [reorderQuestionsModalOpen, configuration, sectionIdxToReorder]);

  const moveSection = (index, direction) => {
    setReorderList((prev) => {
      const list = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= list.length) return list;
      const temp = list[index];
      list[index] = list[newIndex];
      list[newIndex] = temp;
      return list;
    });
    setReorderIndexList((prev) => {
      const idxs = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= idxs.length) return idxs;
      const temp = idxs[index];
      idxs[index] = idxs[newIndex];
      idxs[newIndex] = temp;
      return idxs;
    });
  };

  const moveQuestion = (index, direction) => {
    setReorderedQuestions((prev) => {
      const list = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= list.length) return list;
      const temp = list[index];
      list[index] = list[newIndex];
      list[newIndex] = temp;
      return list;
    });
    setReorderQuestionIndexList((prev) => {
      const idxs = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= idxs.length) return idxs;
      const temp = idxs[index];
      idxs[index] = idxs[newIndex];
      idxs[newIndex] = temp;
      return idxs;
    });
  }

  const saveReorderedSections = () => {
    if (onReorderSections) {
      onReorderSections(reorderIndexList);
    }
    setReorderSectionsModalOpen(false);
  };

  const saveReorderedQuestions = () => {
    if (onReorderQuestions && sectionIdxToReorder !== null && sectionIdxToReorder !== undefined) {
      onReorderQuestions(sectionIdxToReorder, reorderQuestionIndexList);
    }
    setReorderQuestionsModalOpen(false);
  }

  const reorderSectionsModal = (
    <Modal isOpen={reorderSectionsModalOpen} onClose={() => setReorderSectionsModalOpen(false)}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reorder Sections</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {reorderList.map((section, index) => (
            <Flex key={index} align="center" py={4} borderBottomWidth={index !== reorderList.length - 1 ? 1 : 0}>
              <Text flex="1" mr={4} noOfLines={2}>{section.title}</Text>
              <Flex gap={2}>
                <IconButton
                  aria-label={`Move ${section.title} up`}
                  icon={<ChevronUpIcon />}
                  size="sm"
                  onClick={() => moveSection(index, -1)}
                  isDisabled={index === 0}
                  variant="ghost"
                />
                <IconButton
                  aria-label={`Move ${section.title} down`}
                  icon={<ChevronDownIcon />}
                  size="sm"
                  onClick={() => moveSection(index, 1)}
                  isDisabled={index === reorderList.length - 1}
                  variant="ghost"
                />
              </Flex>
            </Flex>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={saveReorderedSections}>
            Save
          </Button>
          <Button variant="ghost" onClick={() => setReorderSectionsModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  const reorderQuestionsModal = (
    <Modal isOpen={reorderQuestionsModalOpen} onClose={() => setReorderQuestionsModalOpen(false)}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Reorder Questions in {configuration[sectionIdxToReorder]?.title}</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          {reorderQuestions.map((question, index) => (
            <Flex key={index} align={"center"} py={4} borderBottomWidth={index !== reorderQuestions.length -1 ? 1 : 0}>
              <Text flex={"1"} mr={4} noOfLines={2}>{question.name}</Text>
              <Flex gap={2}>
                <IconButton
                  aria-label={`Move ${question.name} up`}
                  icon={<ChevronUpIcon/>}
                  size={"sm"}
                  onClick={() => moveQuestion(index, -1)}
                  isDisabled={index === 0}
                  variant={"ghost"}
                />
                <IconButton
                  aria-label={`Move ${question.name} down`}
                  icon={<ChevronDownIcon />}
                  size="sm"
                  onClick={() => moveQuestion(index, 1)}
                  isDisabled={index === reorderQuestions.length - 1}
                  variant="ghost"
                />
              </Flex>
            </Flex>
          ))}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="blue" mr={3} onClick={saveReorderedQuestions}>
            Save
          </Button>
          <Button variant="ghost" onClick={() => setReorderQuestionsModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )

  const handleRadioChange = (id, value) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: value,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  const handleCheckboxChange = (id, value) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: value,
      };
      onChange(newSettings);
      return newSettings;
    })
  }

  const handleSwitchChange = (id, event) => {
    const isChecked = event.target.checked;
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: isChecked ? "" : undefined,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  const handleInputChange = (id, event) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: event.target.value,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  const handleSelectChange = (id, selectedOptions, isMulti) => {
    setSettings((prevSettings) => {
      const newSettings = {
        ...prevSettings,
        [id]: isMulti
          ? selectedOptions
            ? selectedOptions.map((option) => option.value)
            : []
          : selectedOptions.value,
      };
      onChange(newSettings);
      return newSettings;
    });
  };

  useEffect(() => {
    const changesDetected = !_.isEqual(settings, originalSettings);
    setHasChanges(changesDetected);
  }, [settings, originalSettings, configuration]);

  const bgColor = useColorModeValue("gray.100", "gray.700");
  const hintColor = useColorModeValue("gray.500", "gray.400");

  const deleteSecion = (index) => {
    configuration.splice(index, 1);
  }

  return (
    <Box p={4} w="100%" align="left">
      {configuration.map((section, index) => (
        <Box key={index} mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Flex direction={'row'}>
          <Flex direction={"column"} gap={2} mt={2} mb={2}>
            <Text mb={4}>{section.title}</Text>
            <Text mb={4}>{section.description}</Text>
          </Flex>
            <Flex justifyContent={'flex-end'} ml={'auto'}>
              <Menu>
                <MenuButton as={IconButton} aria-label="Options" icon={<FontAwesomeIcon icon={faEllipsisV}/>}
                            variant="ellipsis"/>
                <MenuList>
                  <MenuItem id={`reorder-sections-${index}`} onClick={() => setReorderSectionsModalOpen(true)}>
                    Reorder Sections
                  </MenuItem>
                  <MenuItem id={`reorder-questions-${index}`} onClick={() => {
                    setSectionIdxToReorder(index);
                    setReorderQuestionsModalOpen(true);
                  }}>
                    Reorder Questions
                  </MenuItem>
                  <MenuItem id={`delete-section-${index}`} onClick={() => deleteSecion(index)}>
                    Delete Section
                  </MenuItem>
                </MenuList>
              </Menu>
            </Flex>
        </Flex>
          {section.inputs.map((input, idx) => (
            <Box key={idx} mb={4}>
              <Text mb={1}>{input.name} {input.required && (
                <Text as="span" color="red.500" ml={1}>
                  *
                </Text>
              )}</Text>
              {input.hint && (
                <Text fontSize="sm" mb={2} color={hintColor}>
                  {input.hint}
                </Text>
              )}
              {input.type === "radio" && (
                <Flex align="center">
                  <RadioGroup
                    isDisabled={true}
                    onChange={(value) => handleRadioChange(input.id, value)}
                    value={settings[input.id] || ""}
                  >
                    <VStack align="start">
                      {input.options.map((option) => (
                        <Radio key={option.value} value={option.value}>
                          {option.label}
                        </Radio>
                      ))}
                    </VStack>
                  </RadioGroup>
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon/>}
                    ml={4}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
              {input.type === "checkbox" && (
                <Flex align={"center"}>
                  <CheckboxGroup
                    isDisabled={true}
                    onChange={(value) => handleCheckboxChange(input.id, value)}
                    value={settings[input.id] || ""}
                  >
                    <VStack align={"start"}>
                      {input.options.map((option) => (
                        <Checkbox key={option.value} value={option.value}>
                          {option.label}
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon/>}
                    ml={4}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
              {input.type === "switch" && (
                <Flex align="center">
                  <Switch
                    isChecked={settings[input.id] !== undefined}
                    onChange={(event) => handleSwitchChange(input.id, event)}
                    isDisabled={true}
                  >
                    {input.name}
                  </Switch>
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon/>}
                    ml={2}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
              {input.type === "switchwt" && (
                <>
                  <Flex align="center">
                    <Switch
                      isChecked={settings[input.id] !== undefined}
                      onChange={(event) => handleSwitchChange(input.id, event)}
                      isDisabled={true}
                    >
                      {input.name}
                    </Switch>
                    <IconButton
                      aria-label="Delete field"
                      icon={<DeleteIcon/>}
                      ml={2}
                      onClick={() => onDeleteField(input.id)}
                    />
                  </Flex>
                  {settings[input.id] !== undefined && (
                    <Input
                      mt={2}
                      value={settings[input.id] || ""}
                      onChange={(event) =>
                        handleSwitchChange(input.id, event)
                      }
                      placeholder={`Enter ${input.name} details`}
                    />
                  )}
                </>
              )}
              {input.type === "input" && (
                <Flex align="center">
                  <Input
                    id={input.id}
                    value={settings[input.id] || ""}
                    onChange={(event) => handleInputChange(input.id, event)}
                    placeholder={input.name}
                    isReadOnly={true}
                  />
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon/>}
                    ml={2}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
              {input.type === "select" && (
                <Flex align="center">
                  <Select
                    styles={{
                      control: (base) => ({
                        ...base,
                        background:
                          colorMode === "light" ? "#F7FAFC" : "#2D3748",
                        borderColor:
                          colorMode === "light" ? "#CBD5E0" : "#4A5568",
                        "&:hover": {
                          borderColor:
                            colorMode === "light" ? "#A0AEC0" : "#718096",
                        },
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: "#fff",
                      }),
                      multiValue: (base) => ({
                        ...base,
                        color: "#fff",
                      }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      menu: (base) => ({
                        ...base,
                        backgroundColor:
                          colorMode === "light" ? "#717274" : "white",
                        boxShadow: "0px 10px 20px rgba(0,0,0,0.2)",
                        border: "1px solid",
                        borderColor:
                          colorMode === "light" ? "#717274" : "#718096",
                        borderRadius: "4px",
                        zIndex: 9999,
                      }),
                      option: (base, state) => ({
                        ...base,
                        color: colorMode === "light" ? "white" : "black",
                        backgroundColor:
                          colorMode === "light" ? "#717274" : "white",
                      }),
                    }}
                    id={input.id}
                    value={input.options.filter((option) =>
                      settings[input.id] && Array.isArray(settings[input.id])
                        ? settings[input.id].includes(option.value)
                        : settings[input.id] === option.value
                    )}
                    onChange={(selectedOptions) =>
                      handleSelectChange(
                        input.id,
                        selectedOptions,
                        input.isMulti
                      )
                    }
                    options={input.options}
                    isMulti={input.isMulti}
                    isDisabled={true}
                  />
                  <IconButton
                    aria-label="Delete field"
                    icon={<DeleteIcon/>}
                    ml={2}
                    onClick={() => onDeleteField(input.id)}
                  />
                </Flex>
              )}
            </Box>
          ))}
          <Button mt={4} onClick={() => onAddInput(index)}>
            Add Question
          </Button>
        </Box>
      ))}
      {reorderSectionsModal}
      {reorderQuestionsModal}
    </Box>
  );
};

function History() {
  const convertToGeneric = (template) => {
    return template?.map((section) => {
      return {
        title: section.title,
        description: section.description || "",
        inputs: section.questions.map((q) => ({
          type:
            q.type === "t"
              ? "input"
              : q.type === "yn"
              ? "switch"
              : q.type === "multiple"
              ? "select"
              : q.type === "ynwt"
              ? "switchwt"
              : q.type,
          options: q?.options?.length ? q?.options : [],
          id: q.id,
          hint: q.hint,
          name: q.name || q.text,
          required: q.required,
        })),
      };
    });
  };
  const bgColor = useColorModeValue("gray.100", "gray.700");

  const toast = useToast();
  const navigate = useNavigate();
  const [template, setTemplate] = useState({ data: [] });
  const [originalTemplate, setOriginalTemplate] = useState({ data: [] });
  const { template_id } = useParams();
  const [isServerResponding, setIsServerResponding] = useState(false);
  const bid = getBid();

  const fetchHistoryTemplate = async () => {
    if (template_id === "new") {
      const newTemplate = { name: "Medical and Dental History", data: [] };
      setTemplate(newTemplate);
      setOriginalTemplate(_.cloneDeep(newTemplate));
      return;
    }
    const response = await cqAPI.get(
      "/settings/history?template_id=" + template_id
    );
    setTemplate(response.data);
    setOriginalTemplate(_.cloneDeep(response.data));
  };

  const updateTemplateField = async (templateObj, field, value) => {
    const response = await cqAPI.put("/settings/history", {
      template_id: templateObj._id,
      field,
      value,
    });
    if (response?.type === "error") {
      if (response.msg) {
        toast({
          title: "Error updating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      return false;
    }
    await fetchHistoryTemplate();
    return true;
  };

  useEffect(() => {
    fetchHistoryTemplate();
  }, [template_id]);

  const updateTemplate = async () => {
    setIsServerResponding(true);
    const response = await cqAPI.put(`/settings/history`, {
      field: "data",
      value: template.data,
      template_id: template._id,
    });
    if (!response) {
      setIsServerResponding(false);
      return false;
    }
    if (response.type === "error") {
      if (response.msg) {
        toast({
          title: "Error updating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      setIsServerResponding(false);
      return false;
    }
    setOriginalTemplate(_.cloneDeep(template));
    setIsServerResponding(false);
    return true;
  };

  const createTemplate = async () => {
    setIsServerResponding(true);
    const response = await cqAPI.post("/settings/history", {
      name: template.name,
      data: template.data,
    });
    if (response.type === "error") {
      if (response.msg) {
        toast({
          title: "Error creating the template.",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
      setIsServerResponding(false);
      return false;
    }
    setOriginalTemplate(_.cloneDeep(template));
    setIsServerResponding(false);
    navigate(`/settings/history/${response.data}?bid=${getBid()}`, {
      replace: true,
    });
    return true;
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState("");
  const [newSectionDescription, setNewSectionDescription] = useState("");
  const [selectedSection, setSelectedSection] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedSection(null);
  };

  const handleTitleChange = (e) => setNewSectionTitle(e.target.value);
  const handleDescriptionChange = (e) =>
    setNewSectionDescription(e.target.value);

  const addSection = () => {
    if (!newSectionTitle.trim()) {
      toast({
        title: "Title is required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const newSection = {
      title: newSectionTitle,
      description: newSectionDescription,
      questions: [],
    };
    setTemplate({ ...template, data: template.data.concat(newSection) });
    closeModal();
  };

  const [deleteField, setDeleteField] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();

  const handleDeleteClick = (field) => {
    setDeleteField(field);
    onOpen();
  };

  const confirmDelete = () => {
    if (deleteField) {
      const updatedTemplate = { ...template };
      updatedTemplate.data.forEach((section) => {
        section.questions = section.questions.filter(
          (q) => q.id !== deleteField
        );
      });
      setTemplate(updatedTemplate);
    }
    onClose();
  };

  const [inputModalOpen, setInputModalOpen] = useState(false);
  const [inputType, setInputType] = useState("t");
  const [inputName, setInputName] = useState("");
  const [inputHint, setInputHint] = useState("");
  const [inputOptions, setInputOptions] = useState("");
  const [inputRequired, setInputRequired] = useState(false);

  const openInputModal = (sectionIndex) => {
    setSelectedSection(sectionIndex);
    setInputModalOpen(true);
  };

  const closeInputModal = () => {
    setInputModalOpen(false);
    setInputType("t");
    setInputName("");
    setInputHint("");
    setInputOptions("");
    setInputRequired(false);
  };

  const handleInputTypeChange = (e) => setInputType(e.target.value);
  const handleInputNameChange = (e) => setInputName(e.target.value);
  const handleInputHintChange = (e) => setInputHint(e.target.value);
  const handleInputOptionsChange = (e) => setInputOptions(e.target.value);
  const handleInputRequiredChange = (e) => setInputRequired(e.target.checked);

  const addInputToSection = () => {
    if (!inputName.trim() || !inputType.trim()) {
      toast({
        title: "Input name and type are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const newInput = {
      type: inputType,
      name: inputName,
      hint: inputHint,
      options:
        inputType === "select" || inputType === "radio" || inputType === "checkbox"
          ? inputOptions
              .split(",")
              .map((option) => ({ label: option.trim(), value: option.trim() }))
          : undefined,
      id: `${selectedSection}-${inputName
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase()}`,
      required: inputRequired,
    };

    //checking if this template section already has an input with same id
    if (
      template.data[selectedSection].questions.some(
        (question) => question.id === newInput.id
      )
    ) {
      toast({
        title: "An input with this name already exists in the section.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const updatedTemplate = { ...template };
    updatedTemplate.data[selectedSection].questions.push(newInput);
    setTemplate(updatedTemplate);
    closeInputModal();
  };

  const hasChanges = !_.isEqual(template, originalTemplate);
  const inputTypes = [
    { value: "radio", label: "Multiple Choice", subLabel: "Only one can be selected" },
    { value: "switch", label: "Switch", subLabel: "Yes / No" },
    { value: "switchwt", label: "Switch with Text", subLabel: "Yes / No " },
    { value: "select", label: "Dropdown", subLabel: "Dropdown with multiple choices" },
    { value: "input", label: "Text", subLabel: "Text field" },
    { value: "checkbox", label: "Checkbox", subLabel: "Select all that Apply" },
  ];

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
    >
      <Flex
        justify={"flex-end"}
        textAlign="right"
        px={4}
        borderRadius="md"
      >
        <Button onClick={() =>navigate(-1)}>
          Close
        </Button>
      </Flex>

      <Box p={4} w="100%" align="left">
        <Box mb={4} p={4} bgColor={bgColor} borderRadius="md">
          <Flex justifyContent="space-between" alignItems="center">
            <Flex w="250px">
              <EditableText
                onSave={(newValue) => {
                  setTemplate({ ...template, name: newValue });
                  return true;
                }}
                initialValue={template.name}
                placeholder="Name"
                pattern={/[^\ ]{3,}/}
                invalid="more than 3 character."
              />
            </Flex>
            <Flex>
              {template_id !== "new" && (
                <Tooltip label="Only the owner can update the template." isDisabled={template.bid === bid}>
                  <Button
                    leftIcon={
                      isServerResponding ? <Spinner size="sm" /> : <CheckIcon />
                    }
                    onClick={updateTemplate}
                    marginRight="10px"
                    isDisabled={!hasChanges || isServerResponding || template.bid !== bid}
                  >
                    Update
                  </Button>
                </Tooltip>
              )}
              <Button
                leftIcon={
                  isServerResponding ? <Spinner size="sm" /> : <CheckIcon />
                }
                onClick={createTemplate}
                marginRight="10px"
                isDisabled={!hasChanges || isServerResponding}
              >
                Save as New
              </Button>
              <Button leftIcon={<AddIcon />} onClick={openModal}>
                Add Section
              </Button>
            </Flex>
          </Flex>
          <HistorySettingsControls
            template={template}
            updateTemplateField={updateTemplateField}
          />
          <Flex direction={'column'}mt={4} gap={2}>
          <Text>Questionnaire description and instruction (This will show at the top of the form)</Text>
            <Flex direction={"row"} >
              <EditableText
                initialValue={template.description}
                placeholder='Description and instruction'
                onSave={(value) => updateTemplateField(template, 'description', value)}
                pattern={/[^\ ]{3,}/}
              />
            </Flex>
          </Flex>
      </Box>
      </Box>

      <GenericSettings
        configuration={convertToGeneric(template.data)}
        onDeleteField={handleDeleteClick}
        onAddInput={openInputModal}
        onChange={(newSettings) =>
          setTemplate({ ...template, data: newSettings })
        }
        onReorderSections={(indexList) => {
          setTemplate({ ...template, data: indexList.map((i) => template.data[i]) });
        }}
        onReorderQuestions={(sectionIdx, indexList) => {
          const updated = { ...template };
          const sec = updated.data[sectionIdx];
          if (sec && Array.isArray(sec.questions)) {
            sec.questions = indexList.map((i) => sec.questions[i]);
          }
          setTemplate(updated);
        }}
      />

      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Section</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input value={newSectionTitle} onChange={handleTitleChange} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Description</FormLabel>
              <Input
                value={newSectionDescription}
                onChange={handleDescriptionChange}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={addSection}>
              Add
            </Button>
            <Button variant="ghost" onClick={closeModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Field
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this field? This action cannot be
              undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={inputModalOpen} onClose={closeInputModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Question</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Question Type</FormLabel>
              <Select
                value={inputTypes.find(opt => opt.value === inputType)}
                onChange={(opt) => handleInputTypeChange({ target: { value: opt.value } })}
                options={inputTypes}
                getOptionLabel={(opt) => (
                  <Flex direction={'row'}>
                    <Text color={theme.colors.lightText}>{opt.label}</Text>
                    <div style={{ alignSelf:'center', fontSize: "0.8em", color: "gray" }}>{" - " + opt.subLabel}</div>
                  </Flex>
                )}
                chakraStyles={{
                  container: (provided) => ({ ...provided, width: "100%" }),
                }}
                isSearchable={false}
              />
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel>Question or Instruction</FormLabel>
              <Input value={inputName} onChange={handleInputNameChange} />
            </FormControl>
            <FormControl mt={4}>
              <FormLabel>Hint</FormLabel>
              <Input value={inputHint} onChange={handleInputHintChange} />
            </FormControl>
            {(inputType === "select" || inputType === "radio" || inputType === "checkbox") && (
              <FormControl mt={4}>
                <FormLabel>Options (comma separated)</FormLabel>
                <Input
                  value={inputOptions}
                  onChange={handleInputOptionsChange}
                />
              </FormControl>
            )}
            <FormControl display='flex' alignItems='center' mt={4}>
              <FormLabel htmlFor='required-field' mb='0'>
                Required
              </FormLabel>
              <Switch id='required-field' onChange={handleInputRequiredChange} isChecked={inputRequired}/>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={addInputToSection}>
              Add
            </Button>
            <Button variant="ghost" onClick={closeInputModal}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
}

export default History;
